import { Flame, Music, Disc, BookOpen, Sparkles, SlidersHorizontal } from 'lucide-react';
import type { ElementType } from 'react';
import type { BlockCategory } from '../types';

export interface BlockCategoryConfig {
  id: BlockCategory;
  label: string;
  englishLabel: string;
  token: 'accent' | 'primary' | 'secondary' | 'info' | 'cat-tone' | 'base-content/60';
  tone: 'accent' | 'primary' | 'secondary' | 'info' | 'cat-tone' | 'neutral';
  badgeClass: string;
  textClass: string;
  bgClass: string;
  dotClass: string;
  hex: {
    dark: string;
    light: string;
  };
  icon: ElementType;
}

export const BLOCK_CATEGORY_MAP: Record<BlockCategory, BlockCategoryConfig> = {
  WARMUP: {
    id: 'WARMUP',
    label: 'گرم کردن',
    englishLabel: 'Warmup',
    token: 'accent',
    tone: 'accent',
    badgeClass: 'bg-accent/14 text-accent-soft border-accent/40',
    textClass: 'text-accent-soft',
    bgClass: 'bg-accent',
    dotClass: 'bg-accent',
    hex: {
      dark: '#F59E0B',
      light: '#D97706',
    },
    icon: Flame,
  },
  REPERTOIRE: {
    id: 'REPERTOIRE',
    label: 'رپرتوار',
    englishLabel: 'Repertoire',
    token: 'primary',
    tone: 'primary',
    badgeClass: 'bg-primary/15 text-primary-soft border-primary/40',
    textClass: 'text-primary-soft',
    bgClass: 'bg-primary',
    dotClass: 'bg-primary',
    hex: {
      dark: '#3B82F6',
      light: '#2563EB',
    },
    icon: Music,
  },
  IMPROVISATION: {
    id: 'IMPROVISATION',
    label: 'بداهه‌نوازی',
    englishLabel: 'Improvisation',
    token: 'secondary',
    tone: 'secondary',
    badgeClass: 'bg-secondary/12 text-secondary-soft border-secondary/40',
    textClass: 'text-secondary-soft',
    bgClass: 'bg-secondary',
    dotClass: 'bg-secondary',
    hex: {
      dark: '#10B981',
      light: '#059669',
    },
    icon: Sparkles,
  },
  SIGHT_READING: {
    id: 'SIGHT_READING',
    label: 'دیدخوانی',
    englishLabel: 'Sight Reading',
    token: 'info',
    tone: 'info',
    badgeClass: 'bg-info/12 text-info-soft border-info/40',
    textClass: 'text-info-soft',
    bgClass: 'bg-info',
    dotClass: 'bg-info',
    hex: {
      dark: '#06B6D4',
      light: '#0891B2',
    },
    icon: BookOpen,
  },
  TONE_DIALING: {
    id: 'TONE_DIALING',
    label: 'تنظیم صدا',
    englishLabel: 'Tone Dialing',
    token: 'cat-tone',
    tone: 'cat-tone',
    badgeClass: 'bg-cat-tone/15 text-cat-tone border-cat-tone/40',
    textClass: 'text-cat-tone',
    bgClass: 'bg-cat-tone',
    dotClass: 'bg-cat-tone',
    hex: {
      dark: '#A78BFA',
      light: '#7C3AED',
    },
    icon: Disc,
  },
  CUSTOM: {
    id: 'CUSTOM',
    label: 'سفارشی',
    englishLabel: 'Custom',
    token: 'base-content/60',
    tone: 'neutral',
    badgeClass: 'bg-neutral text-base-content/60 border-base-300',
    textClass: 'text-base-content/60',
    bgClass: 'bg-base-content/60',
    dotClass: 'bg-base-content/60',
    hex: {
      dark: '#94A3B8',
      light: '#64748B',
    },
    icon: SlidersHorizontal,
  },
};

export const BLOCK_CATEGORIES: BlockCategory[] = [
  'WARMUP',
  'REPERTOIRE',
  'IMPROVISATION',
  'SIGHT_READING',
  'TONE_DIALING',
  'CUSTOM',
];

export function getBlockCategoryConfig(category?: BlockCategory | null): BlockCategoryConfig {
  if (!category || !(category in BLOCK_CATEGORY_MAP)) {
    return BLOCK_CATEGORY_MAP.CUSTOM;
  }
  return BLOCK_CATEGORY_MAP[category];
}

export function getBlockCategoryLabel(category?: BlockCategory | null): string {
  return getBlockCategoryConfig(category).label;
}

export function getBlockCategoryToken(category?: BlockCategory | null): string {
  return getBlockCategoryConfig(category).token;
}

export function getBlockCategoryTone(category?: BlockCategory | null): BlockCategoryConfig['tone'] {
  return getBlockCategoryConfig(category).tone;
}

export function getBlockCategoryBadgeClass(category?: BlockCategory | null): string {
  return getBlockCategoryConfig(category).badgeClass;
}
